import { PURCHASE_TYPE_UNSET, type Promotion, type PromotionList } from "../../api/promotion.ts";
import { ModuleId } from "../../types/session.ts";

/*
 * Promotion editor rules: per-module options, load normalization and save
 * validation. Pure (no React), so vitest covers them - promotionRules.test.ts.
 */

export interface Option { v: number; l: string }

// "Time(s) per" — DB values match legacy WebAdmin2 (editpromotion.ts:651-658,773):
//   0 = Code, 1 = Card/Account, 2 = First-time only (no picker row, checkbox instead).
export const VALID_TIMES_FOR_CODE = 0;
export const VALID_TIMES_FOR_PER_USER = 1;
export const VALID_TIMES_FOR_FIRST_TIME = 2;

// Per-user limit label depends on module — "Card" for swipe-card modules, "Account"
// for the Access module (matches legacy editpromotion.ts:519-521).
export function getValidTimesForOptions(mod: number): Option[] {
  const perUserLabel = mod === ModuleId.Access ? "Account" : "Card";
  return [
    { v: VALID_TIMES_FOR_CODE, l: "Code" },
    { v: VALID_TIMES_FOR_PER_USER, l: perUserLabel },
  ];
}

// Promotion enum option values are stored 0-based on the server (DVD_Promotions
// columns iPurchaseType / iDiscountType / iDiscountFor / iValidTimesFor). The
// legacy WebAdmin2 picker was 1-based and did +1/-1 at the IO boundary
// (editpromotion.ts:618-621, 771-777); WebAdminCC writes the column value
// directly, so the option `v`s must match the on-disk encoding exactly. Don't
// shift these — the saved record gets interpreted by KioskSuite / cart engines
// using the 0-based values.
export function getAppliesToOptions(mod: number): Option[] | null {
  switch (mod) {
    case ModuleId.Rent:
    case ModuleId.NeoCaddie:
      return [{ v: 0, l: "Rentals" }, { v: 1, l: "Buy Used" }];
    case ModuleId.Banner:
      return [{ v: 0, l: "Any size" }, { v: 1, l: "Small" }, { v: 2, l: "Medium" }, { v: 3, l: "Large" }];
    default:
      return null; // picker hidden; PurchaseType is pinned by normalizePromotion
  }
}

// PurchaseType values SavePromotionInfo (Promotions.vb) accepts per module. It
// rejects anything else with a bare `Return Nothing` - an empty result and no
// error - so a hidden picker must still carry a value from this set. Legacy
// WebAdmin2 sent 0 whenever its picker was hidden.
export function getAllowedPurchaseTypes(mod: number): number[] {
  switch (mod) {
    case ModuleId.Banner:
      return [0, 1, 2, 3];
    case ModuleId.CompuShop:
    case ModuleId.Access:
      return [0];
    default:
      return [0, 1];
  }
}

export function getGivesAsOptions(mod: number): Option[] {
  const base = [{ v: 0, l: "%" }, { v: 1, l: "Amount" }];
  if (mod === ModuleId.Rent || mod === ModuleId.NeoCaddie) {
    return [...base, { v: 2, l: "Flat Price" }];
  }
  if (mod === ModuleId.Access) return base;
  return [...base, { v: 2, l: "Credits" }];
}

export function getDiscountOnOptions(mod: number): Option[] {
  if (mod === ModuleId.Banner) return [{ v: 0, l: "All banners" }, { v: 1, l: "One banner" }];
  return [{ v: 0, l: "Each product in cart" }, { v: 1, l: "One product in cart" }];
}

export function showExtendedDays(mod: number) {
  return mod !== ModuleId.CompuShop && mod !== ModuleId.Access;
}

export function useCounterForMinCart(mod: number) {
  return mod === ModuleId.CompuShop || mod === ModuleId.Access;
}

/**
 * Several enum-coded fields render via `<select value={number}>` with options
 * keyed by numeric IDs. Server records pre-dating the current UI sometimes
 * carry `0` (unset) for these — the browser then shows the first option
 * visually but leaves state at `0`, which is the "fields not load correctly"
 * bug the user hit on `Time(s) per`. Normalize on load so every select has
 * a matching option to highlight, and saves a real value back.
 *
 * PurchaseType: with a picker, a value it doesn't offer becomes the "-- Select --"
 * sentinel so the user has to choose. Without one (CompuShop, Access, and every
 * module getAppliesToOptions doesn't list) a value the server would reject
 * becomes 0, as legacy sent - the sentinel there was invisible and unfixable,
 * and made every Create come back empty.
 *
 * Nullable columns (list ids are NULL for "none" since SavePromotionInfo stopped
 * storing 0) arrive as null; a controlled input needs a value.
 */
export function normalizePromotion(p: Promotion, moduleId: number): Promotion {
  const givesAs = getGivesAsOptions(moduleId).map((o) => o.v);
  const discountOn = getDiscountOnOptions(moduleId).map((o) => o.v);
  // ValidTimesFor: 0=Code, 1=Card/Account, 2=FirstTimeOnly. Anything else → 0.
  const validTimesFor = [VALID_TIMES_FOR_CODE, VALID_TIMES_FOR_PER_USER, VALID_TIMES_FOR_FIRST_TIME].includes(p.ValidTimesFor)
    ? p.ValidTimesFor
    : VALID_TIMES_FOR_CODE;
  const discountType = givesAs.includes(p.DiscountType) ? p.DiscountType : givesAs[0] ?? 1;
  const discountFor = discountOn.includes(p.DiscountFor) ? p.DiscountFor : discountOn[0] ?? 1;

  const appliesTo = getAppliesToOptions(moduleId);
  let purchaseType = p.PurchaseType ?? PURCHASE_TYPE_UNSET;
  if (appliesTo) {
    if (!appliesTo.some((o) => o.v === purchaseType)) purchaseType = PURCHASE_TYPE_UNSET;
  } else if (!getAllowedPurchaseTypes(moduleId).includes(purchaseType)) {
    purchaseType = 0;
  }

  return {
    ...p,
    Title: p.Title ?? "",
    Description: p.Description ?? "",
    ValidDays: p.ValidDays ?? "",
    PurchaseType: purchaseType,
    ValidTimesFor: validTimesFor,
    DiscountType: discountType,
    DiscountFor: discountFor,
    ValidOnKiosk: p.ValidOnKiosk ?? 0,
    ExtendedDays: p.ExtendedDays ?? 0,
    PromotionListID: p.PromotionListID ?? 0,
    PromotionLocationListID: p.PromotionLocationListID ?? 0,
    OperatorID: p.OperatorID ?? 0,
  };
}

export function parseValidDays(s: string): number[] {
  return s.split(",").map((d) => d.trim()).filter((d) => d !== "").map(Number).filter((n) => !isNaN(n));
}

/* ── Validation (shared, pure) ──────────────────────── */

const isWhole = (n: number, min: number) => Number.isInteger(n) && n >= min;

/**
 * Everything SavePromotionInfo checks, plus what legacy WebAdmin2 checked, so a
 * request only goes out when the server will take it. The server answers a
 * failed check with an empty result and no error, so a miss here is silent.
 * `lists` is null until the list fetch succeeded; list ids aren't checked then.
 */
export function validatePromotion(
  promo: Promotion, moduleId: number, locationMode: "kiosk" | "list", lists: PromotionList[] | null,
): string | null {
  if (!promo.Title.trim()) return "Name is required.";

  if (!getAllowedPurchaseTypes(moduleId).includes(promo.PurchaseType)) {
    return getAppliesToOptions(moduleId)
      ? "Please select what the promotion applies to."
      : `'Applies to' value ${promo.PurchaseType} is not valid for this module.`;
  }

  if (locationMode === "kiosk") {
    if (!isWhole(promo.ValidOnKiosk, 0)) return "Kiosk ID must be 0 (all kiosks) or a kiosk ID.";
  } else if (promo.PromotionLocationListID > 0 && lists
    && !lists.some((l) => l.sType !== "PROD" && l.iPromotionListID === promo.PromotionLocationListID)) {
    return "The selected location list no longer exists. Pick another one.";
  }

  if (promo.PromotionListID > 0 && lists
    && !lists.some((l) => l.sType === "PROD" && l.iPromotionListID === promo.PromotionListID)) {
    return "The selected product list no longer exists. Pick another one.";
  }

  if (![VALID_TIMES_FOR_CODE, VALID_TIMES_FOR_PER_USER, VALID_TIMES_FOR_FIRST_TIME].includes(promo.ValidTimesFor)) {
    return "Please select what 'Time(s) per' counts.";
  }
  const firstTimeOnly = promo.ValidTimesFor === VALID_TIMES_FOR_FIRST_TIME;
  if (!firstTimeOnly && !isWhole(promo.ValidTimes, 0)) {
    return "'Can be used' must be 0 (unlimited) or a whole number.";
  }

  if (!getGivesAsOptions(moduleId).some((o) => o.v === promo.DiscountType)) {
    return "Please select what the promotion gives.";
  }
  if (!Number.isFinite(promo.Discount) || promo.Discount < 0) {
    return "'Promotion gives' cannot be negative.";
  }
  if (promo.DiscountType === 0 && promo.Discount > 100) {
    return "A % discount cannot be more than 100.";
  }
  if (!getDiscountOnOptions(moduleId).some((o) => o.v === promo.DiscountFor)) {
    return "Please select what the discount is on.";
  }

  if (showExtendedDays(moduleId) && !isWhole(promo.ExtendedDays, 0)) {
    return "'Extended days' must be 0 or more.";
  }

  if (!isWhole(promo.DiscountMinCart, 0)) return "'Min cart' must be 0 or more.";
  if (!useCounterForMinCart(moduleId)) {
    if (!isWhole(promo.DiscountMaxCart, -1)) return "'Max cart' must be -1 (no limit) or more.";
    if (promo.DiscountMinCart > 0 && promo.DiscountMaxCart > 0
      && promo.DiscountMinCart > promo.DiscountMaxCart) {
      return "Min cart cannot be greater than max cart.";
    }
  }

  if (promo.DiscountFor === 1 && promo.DiscountMinCart > 1) {
    return "'One product in cart' discount conflicts with min cart > 1.";
  }

  if (parseValidDays(promo.ValidDays).length === 0) return "Select at least one valid day.";

  return null;
}

/** What the dirty check compares: the fields plus the Target toggle, which isn't one. */
export function snapshot(p: Promotion, locationMode: "kiosk" | "list"): string {
  return JSON.stringify({ p, locationMode });
}

